import React, { forwardRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import RenderLabel from "./RenderLabel";
import RenderWidgets from "./RenderWigets";
import { getNestedValue } from "@/utils/helper/form";
import { useFormChangeWarning } from "@/hooks/useFormChangeWarning";
import { useSelector } from "react-redux";
import { dashboardSelector } from "@/store/features/dashboardSlice";

export interface FormRenderRef {
  submitForm: () => void;
  dirtyFields: any;
}

interface RenderFormWithRefProps {
  formConfig: any;
  handleFormSubmit: (data: any) => void;
  className?: string;
  formClassName?: string;
  children?: React.ReactNode;
  formData: any;
}

const RenderFormWithRef = forwardRef<FormRenderRef, RenderFormWithRefProps>(
  ({ formConfig, handleFormSubmit, className = "p-6", formClassName = "", children, formData }, ref) => {
    const methods = useForm({ mode: "all", reValidateMode: "onChange" });
    const { isSideBarExpanded } = useSelector(dashboardSelector);
    const { handleSubmit } = methods;
    useFormChangeWarning(Object.keys(methods.formState.dirtyFields).length > 0);

    // Expose submit function to parent
    React.useImperativeHandle(ref, () => ({
      submitForm: () => {
        handleSubmit(handleFormSubmit)();
      },
      get dirtyFields() {
        return methods.formState.dirtyFields;
      },
    }));

    return (
      <div className={className}>
        <FormProvider {...methods}>
          <form className={`flex flex-col w-full gap-y-6 ${formClassName}`} onSubmit={(e) => e.preventDefault()}>
            {formConfig.sections.map((section: any, sectionIndex: number) => (
              <div
                className={`grid grid-cols-1 ${isSideBarExpanded ? "xl:grid-cols-2  2.5xl:grid-cols-3" : "llg:grid-cols-2 1.5xl:grid-cols-3"}   gap-6`}
                key={sectionIndex}
              >
                {section.widgets?.map((widget: any) => {
                  if (widget?.dependentField) {
                    const dependentFieldValue = methods.watch(widget.dependentField);
                    const shouldHideWidget = !dependentFieldValue || (widget?.dependentFieldValue && widget.dependentFieldValue !== dependentFieldValue);
                    if (shouldHideWidget) return null;
                  }

                  const defaultValue = widget.apiField ? getNestedValue(formData, widget.apiField) : undefined;
                  return (
                    <div className={`flex flex-col gap-1 md:min-w-350 ${widget.className || ""}`} key={widget.field}>
                      {formConfig?.showLabel && !widget?.hideLabel && (
                        <RenderLabel
                          label={widget.label}
                          required={widget?.validation?.required}
                          labelInfo={widget?.labelInfo}
                          className="text-sm text-gray-600"
                        />
                      )}
                      <RenderWidgets widget={widget} defaultValue={defaultValue} />
                    </div>
                  );
                })}
              </div>
            ))}
            {children}
          </form>
        </FormProvider>
      </div>
    );
  }
);

RenderFormWithRef.displayName = "RenderFormWithRef";

export default RenderFormWithRef;
